import dayjs from "dayjs";
import { useInfiniteQuery } from "react-query";
import { supabase } from "../../../config/supabaseClient";
import { useCallback, useMemo, useRef } from "react";
import {
  mapReelsFilterToFeedTypes,
  normalizeFilterForType,
} from "../../helpers/carouselHelper";
import type { ContentFeedDateWindow } from "../../../Screens/DashboardView/StayUpToDate/AddComponentPicker/contentFeedTimeframe";
import {
  CONTENT_FEED_ALL_FEED_TYPES,
  CONTENT_FEED_ALL_SOCIALS,
} from "../../../Screens/DashboardView/StayUpToDate/AddComponentPicker/contentFeedSelection";
import {
  normalizeFeedOrderType,
  sortFeedRowsByPostDate,
} from "../../../Screens/DashboardView/MasterTemplateEditor/feedOrderUtils";

type ShoppableStatusFilter = "all" | "shoppable" | "non-shoppable";

const EMPTY_DATE_WINDOW: ContentFeedDateWindow = {
  applyFilter: false,
  startDate: null,
  endDate: null,
  selectedDates: null,
  componentType: null,
  daysNumber: [],
  selectedDays: [],
};

const hasValidShopLink = (value: any): boolean => {
  if (!value) return false;
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.some((entry) => hasValidShopLink(entry));
  }
  if (typeof value === "object") {
    return Object.values(value).some((entry) => hasValidShopLink(entry));
  }
  return Boolean(value);
};

const isPostShoppable = (post: any): boolean => {
  if (!post) return false;
  if (typeof post?.is_shoppable === "boolean") return post.is_shoppable;
  if (typeof post?.isShoppable === "boolean") return post.isShoppable;
  if (typeof post?.shoppable === "boolean") return post.shoppable;

  if (typeof post?.shoppable === "string") {
    const normalized = post.shoppable.trim().toLowerCase();
    if (["true", "1", "yes"].includes(normalized)) return true;
    if (["false", "0", "no"].includes(normalized)) return false;
  }

  if (typeof post?.shoppable_flag === "boolean") return post.shoppable_flag;

  return (
    hasValidShopLink(post?.shop_link) ||
    hasValidShopLink(post?.shopLink) ||
    hasValidShopLink(post?.shop_url) ||
    hasValidShopLink(post?.shopUrl) ||
    hasValidShopLink(post?.product_link) ||
    hasValidShopLink(post?.productLink)
  );
};

const applyShoppableFilter = (
  items: any[],
  status: string
): any[] => {
  if (!Array.isArray(items) || status === "all") {
    return items;
  }

  return items.filter((item) => {
    const shoppable = isPostShoppable(item);
    return status === "shoppable" ? shoppable : !shoppable;
  });
};

export function useLoadTypeContentData(
  username: any,
  user_id: any,
  platform: any,
  type: any,
  removedPostIds: any,
  order: string,
  customOrderPostIds: any = [],
  shoppableFilter?: string,
  pinnedPostIds?: any[],
  shoppableStatusFilter: string = "all",
  queryScope: string = "default",
  instanceKey: string = "default",
  enabled: boolean = true,
  dateWindow: ContentFeedDateWindow = EMPTY_DATE_WINDOW,
) {
  const POSTS_PER_PAGE = 100;
  const skipRemovedPostFiltering = queryScope === "edit-modal";
  const normalizedOrder = useMemo(() => {
    return normalizeFeedOrderType(order);
  }, [order]);
  // Custom order is applied on the posts already loaded. Switching to Custom
  // or dragging must not start a new feed request (that flashes the preview).
  const fetchOrderRef = useRef(
    normalizedOrder === "CUSTOM" ? "DESC" : normalizedOrder,
  );
  if (normalizedOrder !== "CUSTOM") {
    fetchOrderRef.current = normalizedOrder;
  }
  const fetchOrder =
    normalizedOrder === "CUSTOM" ? fetchOrderRef.current : normalizedOrder;
  // Memoize the removedPostIds processing
  const processedRemovedPostIds: any = useMemo(() => {
    if (removedPostIds && removedPostIds !== "") {
      return new Set(
        removedPostIds.split(",").filter((elem: any) => elem !== "")
      );
    }
    return null;
  }, [removedPostIds,shoppableStatusFilter]);

  const normalizedTypeArray = useMemo(() => {
    const base = Array.isArray(type) ? type : [type];
    return base
      .map((entry: any) => entry && entry.toString().toUpperCase())
      .filter(Boolean);
  }, [type]);

  const isReelsType = normalizedTypeArray.includes("REELS");

  const normalizedFilter = useMemo(
    () =>
      normalizeFilterForType(
        shoppableFilter,
        isReelsType ? "REELS" : normalizedTypeArray[0]
      ),
    [shoppableFilter, isReelsType, normalizedTypeArray]
  );

  const applyDateFilter = Boolean(dateWindow?.applyFilter);
  const windowStart = dateWindow?.startDate || null;
  const windowEnd = dateWindow?.endDate || null;
  const windowComponentType = dateWindow?.componentType || null;
  const windowSelectedDates = dateWindow?.selectedDates || null;

  // Memoize date calculations for STORY type (legacy hardcoded 7d when no timeframe)
  const storyDates = useMemo(() => {
    if (type !== "STORY") return null;
    if (applyDateFilter && windowStart && windowEnd) {
      return { today: windowEnd, yesterday: windowStart };
    }
    return {
      today: dayjs().utc().format("YYYY-MM-DD"),
      yesterday: dayjs().utc().subtract(7, "day").format("YYYY-MM-DD"),
    };
  }, [type, applyDateFilter, windowStart, windowEnd]);

  // Memoize the getPaginatedFeed function
  const getPaginatedFeed = useCallback(
    async ({ pageParam = 1 }) => {
      try {
        // Validate required parameters
        if (!username || !user_id) {
          throw new Error("Missing required parameters: username or user_id");
        }

        let data = null,
          error = null,
          total_count = 0;
        let post_ids: any[] = [];
        const offset = (pageParam - 1) * POSTS_PER_PAGE;

        if (
          applyDateFilter &&
          windowComponentType === "daywise" &&
          (!windowSelectedDates || windowSelectedDates.length === 0)
        ) {
          return { data: [], total_count: 0, originalDataLength: 0, post_ids: [] };
        }

        if (type === "STORY") {
          // Legacy (no timeframe): keep hardcoded ~7d window on *_new (dates unused in SQL today).
          // With timeframe: use *_dated so Yesterday / This Week / This Month / By the Day apply.
          const today =
            storyDates?.today ||
            windowEnd ||
            dayjs().utc().format("YYYY-MM-DD");
          const yesterday =
            storyDates?.yesterday ||
            windowStart ||
            dayjs().utc().subtract(7, "day").format("YYYY-MM-DD");
          const useDatedStoryRpc = applyDateFilter;
          const { data: content, error: contentError } = await supabase.rpc(
            useDatedStoryRpc
              ? "get_storytypecontenttteonord_pin_dated"
              : "get_storytypecontenttteonord_pin_new",
            useDatedStoryRpc
              ? {
                  userid: user_id,
                  user_name: username,
                  platform_name: platform,
                  feed_type: type,
                  start_date: yesterday,
                  end_date: today,
                  order_type: fetchOrder,
                  custom_order: normalizedOrder === "CUSTOM" ? [] : customOrderPostIds,
                  limit_num: POSTS_PER_PAGE,
                  offset_num: offset,
                  pinned_post_ids: pinnedPostIds,
                  shoppable_status_filter: shoppableStatusFilter,
                  component_type: windowComponentType,
                  selected_dates: windowSelectedDates,
                  user_timezone: Intl.DateTimeFormat().resolvedOptions()
                    .timeZone,
                }
              : {
                  userid: user_id,
                  user_name: username,
                  platform_name: platform,
                  feed_type: type,
                  start_date: yesterday,
                  end_date: today,
                  order_type: fetchOrder,
                  custom_order: normalizedOrder === "CUSTOM" ? [] : customOrderPostIds,
                  limit_num: POSTS_PER_PAGE,
                  offset_num: offset,
                  pinned_post_ids: pinnedPostIds,
                  shoppable_status_filter: shoppableStatusFilter,
                }
          );
          error = contentError;
          // RPC may return { data, post_ids, total } or the array directly
          if (Array.isArray(content)) {
            data = content;
            total_count = content.length;
            post_ids = (content as any[]).map((p: any) => p?.post_id).filter(Boolean);
          } else if (content && typeof content === "object") {
            data = content.data ?? null;
            total_count = content.total ?? content.length ?? 0;
            post_ids = content.post_ids || [];
          }
        } else if (type === "FEATURE") {
          const { data: content, error: contentError } = await supabase.rpc(
            "get_featuredddataaeeord_pin_new",
            {
              userid: user_id,
              filter: [
                "FEED",
                "REELS",
                "STORY",
                "HIGHLIGHTS",
                "PIN",
                "VIDEO",
                "PODCAST",
                "TIKTOK",
                "MAIL",
                "TWEET",
                "PRODUCT",
                "POST",
              ],
              query_str: "",
              user_name: username,
              socials: [
                "instagram",
                "youtube",
                "tiktok",
                "podcast",
                "pinterest",
                "newsletter",
                "twitter",
                "LTK",
                "Amazon",
                "ShopMy",
                "MISC",
                "linkedin",
                "MISC-content",
              ],
              order_type: fetchOrder,
              custom_order: normalizedOrder === "CUSTOM" ? [] : customOrderPostIds,
              limit_num: POSTS_PER_PAGE,
              offset_num: offset,
              pinned_post_ids: pinnedPostIds,
              shoppable_status_filter: shoppableStatusFilter,
            }
          );

          const rows = Array.isArray(content)
            ? content
            : Array.isArray(content?.data)
              ? content.data
              : [];
          data = rows;
          error = contentError;
          total_count = content?.total || content?.length || rows.length;
          post_ids =
            content?.post_ids ||
            rows
              .map((p: { post_id?: unknown; id?: unknown }) => p?.post_id ?? p?.id)
              .filter(Boolean);

        } else if (type === "ALL") {
          // Recent Content platform "All" — active posts across content platforms (not Featured).
          const lcUser = String(username || "").trim().toLowerCase();
          const allBase = {
            userid: user_id,
            filter: [...CONTENT_FEED_ALL_FEED_TYPES],
            query_str: "",
            user_name: lcUser,
            socials: [...CONTENT_FEED_ALL_SOCIALS],
            order_type: fetchOrder,
            custom_order: normalizedOrder === "CUSTOM" ? [] : customOrderPostIds,
            limit_num: POSTS_PER_PAGE,
            offset_num: offset,
            pinned_post_ids: pinnedPostIds,
            shoppable_status_filter: shoppableStatusFilter,
          };
          const { data: content, error: contentError } = applyDateFilter
            ? await supabase.rpc("get_content_all_platforms_dated", {
                ...allBase,
                start_date: windowStart,
                end_date: windowEnd,
                component_type: windowComponentType,
                selected_dates: windowSelectedDates,
                user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              })
            : await supabase.rpc("get_content_all_platforms_new", allBase);

          data = content?.data;
          error = contentError;
          total_count = content?.total || content?.length || 0;
          post_ids = content?.post_ids || [];

        } else {
          // Check if platform is provided for other types
          if (!platform && type !== "FEATURE" && type !== "ALL") {
            console.warn(`Platform parameter is required for type: ${type}`);
            return { data: [], total_count: 0, originalDataLength: 0 }; // Return empty object instead of throwing error
          }

          if (platform) {
            let feedTypes: string[] = [];
            if (normalizedTypeArray.includes("LINKEDIN")) {
              feedTypes = ["POST"];
            } else if (isReelsType) {
              feedTypes = mapReelsFilterToFeedTypes(normalizedFilter);
            } else if (normalizedTypeArray.length > 0) {
              feedTypes = normalizedTypeArray;
            }

            if (feedTypes.length === 0) {
              feedTypes = ["REELS"];
            }

            const baseParams = {
              userid: user_id,
              user_name: username,
              platform_name: platform,
              feed_type: feedTypes,
              order_type: fetchOrder,
              custom_order: normalizedOrder === "CUSTOM" ? [] : customOrderPostIds,
              limit_num: POSTS_PER_PAGE,
              offset_num: offset,
              pinned_post_ids: pinnedPostIds,
              shoppable_status_filter: shoppableStatusFilter,
            };

            // Instagram Show "All": content RPC does not return stories.
            // Match Add ghost — fetch posts/reels + stories, then merge.
            const showAllStories =
              isReelsType && normalizedFilter === "all" && offset === 0;
            const contentFeedTypes = showAllStories
              ? feedTypes
                  .map((t) => String(t || "").toUpperCase())
                  .filter((t) => t && t !== "STORY")
              : feedTypes;
            const contentParams = {
              ...baseParams,
              feed_type: contentFeedTypes.length
                ? contentFeedTypes
                : feedTypes,
            };

            const { data: content, error: contentError } = applyDateFilter
              ? await supabase.rpc("get_content_type_postsspinn_dated", {
                  ...contentParams,
                  start_date: windowStart,
                  end_date: windowEnd,
                  component_type: windowComponentType,
                  selected_dates: windowSelectedDates,
                  user_timezone: Intl.DateTimeFormat().resolvedOptions()
                    .timeZone,
                })
              : await supabase.rpc(
                  "get_content_type_postsspinn_new",
                  contentParams,
                );

            data = content?.data;
            error = contentError;
            post_ids = content?.post_ids;
            total_count = content?.total;

            if (!error && showAllStories) {
              const today =
                windowEnd || dayjs().utc().format("YYYY-MM-DD");
              const yesterday =
                windowStart ||
                dayjs().utc().subtract(7, "day").format("YYYY-MM-DD");
              const useDatedStoryRpc = applyDateFilter;
              const storyParams = {
                userid: user_id,
                user_name: username,
                platform_name: platform,
                feed_type: "STORY",
                start_date: yesterday,
                end_date: today,
                order_type: fetchOrder,
                custom_order:
                  normalizedOrder === "CUSTOM" ? [] : customOrderPostIds,
                limit_num: POSTS_PER_PAGE,
                offset_num: 0,
                pinned_post_ids: pinnedPostIds,
                shoppable_status_filter: shoppableStatusFilter,
                ...(useDatedStoryRpc
                  ? {
                      component_type: windowComponentType,
                      selected_dates: windowSelectedDates,
                      user_timezone:
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                    }
                  : {}),
              };
              const { data: storyContent, error: storyError } =
                await supabase.rpc(
                  useDatedStoryRpc
                    ? "get_storytypecontenttteonord_pin_dated"
                    : "get_storytypecontenttteonord_pin_new",
                  storyParams,
                );
              if (!storyError) {
                const otherRows = Array.isArray(data) ? data : [];
                const storyRows = Array.isArray(storyContent)
                  ? storyContent
                  : Array.isArray(storyContent?.data)
                    ? storyContent.data
                    : [];
                const seen = new Set<string>();
                const merged: any[] = [];
                for (const row of [...otherRows, ...storyRows]) {
                  const id = String(row?.post_id ?? row?.id ?? "").trim();
                  if (id && seen.has(id)) continue;
                  if (id) seen.add(id);
                  merged.push(row);
                }
                data = sortFeedRowsByPostDate(merged, fetchOrder).slice(
                  0,
                  POSTS_PER_PAGE,
                );
                post_ids = data
                  .map((p: any) => p?.post_id)
                  .filter(Boolean);
                total_count = Math.max(
                  Number(total_count) || 0,
                  storyRows.length,
                  data.length,
                );
              }
            }
          }
        }


        // Handle errors from Supabase
        if (error) {
          console.error("Database error:", error);
          throw new Error(`Database error: ${error.message}`);
        }
        // Process and return the data
        if (data && Array.isArray(data)) {
          let filteredArray =
            type === "STORY"
              ? sortFeedRowsByPostDate(data, fetchOrder)
              : data;
          const originalDataLength = data.length;

          // processedRemovedPostIds is a Set, so use .size and .has() instead of .length and .includes()
          if (processedRemovedPostIds && processedRemovedPostIds.size > 0) {
            filteredArray = data.filter(
              (obj: any) => !processedRemovedPostIds.has(String(obj.post_id))
            );
          }

          const filteredPostIds =
              (filteredArray || [])
                .map((item: any) => item?.post_id)
                .filter((id: any) => Boolean(id));

          return {
            data: filteredArray,
            total_count: total_count,
            originalDataLength: originalDataLength, // Track original length before filtering
            post_ids: filteredPostIds,
          };
        } else {
          // Return empty array for no data instead of throwing
          return { data: [], total_count: 0, originalDataLength: 0, post_ids: [] };
        }
      } catch (err: any) {
        console.error("Error in getPaginatedFeed:", err);
        throw new Error(err.message || "Failed to fetch posts");
      }
    },
    [
      username,
      user_id,
      platform,
      type,
      processedRemovedPostIds,
      fetchOrder,
      normalizedOrder,
      customOrderPostIds,
      normalizedFilter,
      normalizedTypeArray,
      isReelsType,
      pinnedPostIds,
      skipRemovedPostFiltering,
      shoppableStatusFilter,
      storyDates,
      applyDateFilter,
      windowStart,
      windowEnd,
      windowComponentType,
      windowSelectedDates,
    ]
  );

  // Serialize customOrderPostIds for query key to ensure React Query detects changes
  const customOrderKey = useMemo(() => {
    if (!Array.isArray(customOrderPostIds) || customOrderPostIds.length === 0) {
      return "";
    }
    return customOrderPostIds
      .map((id: any) => String(id).trim())
      .filter((id: string) => id !== "")
      .join(",");
  }, [customOrderPostIds]);

  // Serialize removedPostIds for query key to ensure React Query detects changes
  // This ensures the query refetches when removedPostIds changes
  const removedPostIdsKey = useMemo(() => {
    if (!removedPostIds || removedPostIds === "") {
      return "";
    }
    return removedPostIds
      .split(",")
      .map((id: any) => String(id).trim())
      .filter((id: string) => id !== "")
      .sort() // Sort to ensure consistent key regardless of order
      .join(",");
  }, [removedPostIds]);

  const dateWindowKey = useMemo(() => {
    if (!dateWindow?.applyFilter) return "legacy";
    return [
      dateWindow.startDate || "",
      dateWindow.endDate || "",
      dateWindow.componentType || "",
      (dateWindow.selectedDates || []).join(","),
    ].join("|");
  }, [
    dateWindow?.applyFilter,
    dateWindow?.startDate,
    dateWindow?.endDate,
    dateWindow?.componentType,
    dateWindow?.selectedDates,
  ]);

  const queryResult = useInfiniteQuery(
    [
      "typeContentPosts",
      queryScope,
      instanceKey,
      user_id,
      type,
      removedPostIdsKey, // Use serialized string instead of Set for reliable change detection
      fetchOrder,
      platform,
      normalizedFilter,
      // pinnedPostIds intentionally omitted: pin/unpin in the edit modal is client-side UI
      // state until save. Including it in the key refetches from page 1 and drops loaded pages.
      shoppableStatusFilter,
      dateWindowKey,
    ],
    getPaginatedFeed,
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      enabled,
      getNextPageParam: (lastPage: any, allPages: any) => {
        // Check if there are more pages based on:
        // 1. Original data length (before filtering) - if it's less than POSTS_PER_PAGE, no more pages
        // 2. Total loaded items vs total_count
        if (!lastPage || !Array.isArray(lastPage.data) || lastPage.data.length === 0) {
          return undefined;
        }

        // For types that use pagination (REELS, etc.), check original data length
        // If the original data length is less than POSTS_PER_PAGE, we've reached the end
        if (lastPage.originalDataLength !== undefined) {
          if (lastPage.originalDataLength < POSTS_PER_PAGE) {
            return undefined;
          }
        } else {
          // Fallback: check filtered data length (for STORY/FEATURE types that don't paginate)
          if (lastPage.data.length < POSTS_PER_PAGE) {
            return undefined;
          }
        }

        // Check total loaded items against total_count using originalDataLength
        // This accounts for the actual data fetched from the database, not filtered data
        const totalLoaded = allPages.reduce((sum: number, page: any) => {
          // Use originalDataLength if available, otherwise use data length
          const pageDataLength =
            page?.originalDataLength !== undefined
              ? page.originalDataLength
              : page?.data?.length || 0;
          return sum + pageDataLength;
        }, 0);

        // If we've loaded all items based on total_count, stop
        if (typeof lastPage.total_count === "number" && lastPage.total_count > 0 && totalLoaded >= lastPage.total_count) {
          return undefined;
        }

        // Otherwise, return the next page number
        return allPages.length + 1;
      },
      keepPreviousData: true,
      // retry: 1,
      // refetchOnWindowFocus: true,
      // refetchOnMount: true,
      // staleTime: 30000, // Cache data for 30 seconds
      // cacheTime: 300000, // Keep unused data in cache for 5 minutes
    }
  );

  // Transform the result to maintain backward compatibility with existing code
  const firstPage = queryResult.data?.pages?.[0];
  const totalCount =
    firstPage && typeof firstPage === "object" && "total_count" in firstPage
      ? firstPage.total_count
      : 0;

  const orderedQueryData = useMemo(() => {
    const raw = queryResult.data;
    if (!raw || normalizedOrder !== "CUSTOM" || !customOrderKey) return raw;
    const rank = new Map(
      customOrderKey.split(",").map((id, index) => [id, index]),
    );
    const sortRows = (rows: any[]) =>
      [...rows].sort((a, b) => {
        const left = rank.get(String(a?.post_id ?? ""));
        const right = rank.get(String(b?.post_id ?? ""));
        if (left == null && right == null) return 0;
        if (left == null) return 1;
        if (right == null) return -1;
        return left - right;
      });
    return {
      ...raw,
      pages: raw.pages.map((page: any) =>
        Array.isArray(page?.data) ? { ...page, data: sortRows(page.data) } : page,
      ),
    };
  }, [queryResult.data, normalizedOrder, customOrderKey]);

  return {
    ...queryResult,
    data: orderedQueryData,
    total_count: totalCount,
    dataPages: queryResult.data?.pages || [],
    // Do not OR isFetching: refetches would keep loaders stuck on skeleton
    // (same pattern as useLoadYesterdayLinks).
    isLoading: queryResult.isLoading,
    isQueryLoading: queryResult.isLoading,
    isPreviousData: queryResult.isPreviousData,
    refetch: queryResult.refetch,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
  };
}
